/**
 * Responsive width cap for AskGemini chat message bubbles.
 * @remarks
 * The chat surface has two renderers selected purely by window width
 * (`ChatContainer`'s `useMobileScreen()`, narrow at `<= 1280`):
 *
 * - `<= 1280` renders the `MessageBubble` organism.
 * - `> 1280` renders GiftedChat's `Bubble` from `ChatContainer`.
 *
 * This ladder's desktop step still starts at `>= 1280`, so at exactly 1280
 * the narrow renderer takes the desktop fraction.
 *
 * Both must cap the bubble at the same fraction of the window, otherwise a
 * bubble with no cap grows to the full width of its row. SE-7084: the desktop
 * user bubble had no cap at all while the AI bubble did, so a long query
 * rendered as an oversized solid blue rectangle. Keeping the ladder in one
 * place makes that asymmetry impossible to reintroduce.
 */

/** Window width below which a device is treated as a very small phone. */
const VERY_SMALL_SCREEN_WIDTH = 375;

/** Window width below which a device is treated as a phone. */
const MOBILE_SCREEN_WIDTH = 768;

/** Minimum window width for the desktop chat renderer and bubble cap. */
const DESKTOP_SCREEN_WIDTH = 1280;

/** Fraction of the window a bubble may occupy, per breakpoint. */
const BUBBLE_WIDTH_RATIO = {
  /** Very small devices (iPhone SE) need more of the viewport to stay legible. */
  verySmall: 0.85,
  /** Phones. */
  mobile: 0.75,
  /** Desktop — the widest viewports need the tightest fraction. */
  desktop: 0.6,
  /** Tablets and the 768–1279 band. */
  tablet: 0.85,
} as const;

/**
 * Computes the maximum width a chat bubble may occupy for a given window width.
 * @param windowWidth - Current window width in density-independent pixels
 * @returns Maximum bubble width in density-independent pixels
 */
export const bubbleMaxWidth = (windowWidth: number): number => {
  if (windowWidth < VERY_SMALL_SCREEN_WIDTH)
    return windowWidth * BUBBLE_WIDTH_RATIO.verySmall;
  if (windowWidth < MOBILE_SCREEN_WIDTH)
    return windowWidth * BUBBLE_WIDTH_RATIO.mobile;
  if (windowWidth >= DESKTOP_SCREEN_WIDTH)
    return windowWidth * BUBBLE_WIDTH_RATIO.desktop;
  return windowWidth * BUBBLE_WIDTH_RATIO.tablet;
};
